import { dayBounds, gapContaining, spansOverlap, type Span } from '../geometry/span';
import { BlockStatus } from '../model/enums';
import type { Settings } from '../model/settings';
import {
  isLive,
  liveOnly,
  type LiveProject,
  type LiveTimeBlock,
  type ProjectRecord,
  type TimeBlockRecord,
} from '../model/types';
import { addDays, daysBetween } from '../time/date';
import { wallMinOf } from '../time/personal-day';

export const SuggestionKind = {
  FillSlot: 'fillSlot',
  Continue: 'continue',
  Revisit: 'revisit',
  ConfirmDrafts: 'confirmDrafts',
} as const;
export type SuggestionKind = (typeof SuggestionKind)[keyof typeof SuggestionKind];

export type Suggestion =
  | { readonly kind: 'fillSlot'; readonly projectId: string; readonly span: Span }
  | { readonly kind: 'continue'; readonly projectId: string; readonly span: Span }
  | { readonly kind: 'revisit'; readonly projectId: string; readonly span: Span }
  | { readonly kind: 'confirmDrafts'; readonly count: number };

/** Days looked back for the "same slot" frequency and the "unseen project" test. */
export const SLOT_LOOKBACK_DAYS = 3;
export const REVISIT_AFTER_DAYS = 3;
/** A project must fill the slot on at least this many of the look-back days to be suggested. */
export const FILL_SLOT_MIN_DAYS = 2;
/** "Last night": blocks of yesterday ending after 20:00. */
export const CONTINUE_AFTER_MIN = 20 * 60;
export const MAX_SUGGESTIONS = 4;

export interface SuggestInput {
  readonly nowMs: number;
  /** The current personal date. */
  readonly date: string;
  readonly settings: Settings;
  /**
   * Visible blocks per personal date (expired drafts already removed): today's and the recent
   * days'. The window should be long enough for the "unseen project" test (a project never seen
   * in the window is not suggested).
   */
  readonly blocksByDate: Readonly<Record<string, readonly TimeBlockRecord[]>>;
  readonly projects: readonly ProjectRecord[];
  /** Number of drafts of today awaiting confirmation. */
  readonly draftsPending: number;
}

/**
 * The slot the first three suggestions target (2026-10b §3.3): the default-duration window
 * (aligned to the day start) that contains `nowMin`, intersected with the free gap containing
 * now — the window no longer has to be entirely empty, a window partly covered by a neighbour
 * suggests its free remainder. Both ends keep their exact minutes (no step rounding). Null
 * when now lies outside the day or inside a block, or when the intersection is shorter than
 * one time step.
 */
function currentSlot(nowMin: number, settings: Settings, blocks: readonly Span[]): Span | null {
  const bounds = dayBounds(settings.dayStartMin);
  if (nowMin < bounds.startMin || nowMin >= bounds.endMin) return null;
  const length = Math.max(settings.defaultDurationMin, settings.minDurationMin);
  const index = Math.floor((nowMin - bounds.startMin) / length);
  const windowStart = bounds.startMin + index * length;
  const windowEnd = Math.min(windowStart + length, bounds.endMin);
  const gap = gapContaining(nowMin, blocks, bounds);
  if (gap === null) return null;
  const startMin = Math.max(windowStart, gap.startMin);
  const endMin = Math.min(windowEnd, gap.endMin);
  return endMin - startMin >= settings.timeStepMin ? { startMin, endMin } : null;
}

function confirmedOf(blocks: readonly TimeBlockRecord[] | undefined): LiveTimeBlock[] {
  return liveOnly(blocks ?? []).filter((block) => block.data.status === BlockStatus.Confirmed);
}

/** Active project most often present in `slot` over the look-back days (needs enough days). */
function fillSlotProject(
  input: SuggestInput,
  slot: Span,
  activeIds: ReadonlySet<string>,
  sortOf: ReadonlyMap<string, number>,
): string | null {
  const daysByProject = new Map<string, number>();
  const minutesByProject = new Map<string, number>();
  for (let back = 1; back <= SLOT_LOOKBACK_DAYS; back += 1) {
    const seenToday = new Set<string>();
    for (const block of confirmedOf(input.blocksByDate[addDays(input.date, -back)])) {
      if (!activeIds.has(block.data.projectId) || !spansOverlap(block.data, slot)) continue;
      seenToday.add(block.data.projectId);
      const overlap = Math.min(block.data.endMin, slot.endMin) - Math.max(block.data.startMin, slot.startMin);
      minutesByProject.set(block.data.projectId, (minutesByProject.get(block.data.projectId) ?? 0) + overlap);
    }
    for (const projectId of seenToday) {
      daysByProject.set(projectId, (daysByProject.get(projectId) ?? 0) + 1);
    }
  }
  const ranked = [...daysByProject.entries()]
    .filter(([, days]) => days >= FILL_SLOT_MIN_DAYS)
    .sort(
      ([idA, daysA], [idB, daysB]) =>
        daysB - daysA ||
        (minutesByProject.get(idB) ?? 0) - (minutesByProject.get(idA) ?? 0) ||
        (sortOf.get(idA) ?? 0) - (sortOf.get(idB) ?? 0) ||
        (idA < idB ? -1 : 1),
    );
  return ranked[0]?.[0] ?? null;
}

/** Project of the block that ended latest yesterday, after 20:00. */
function continueProject(input: SuggestInput, activeIds: ReadonlySet<string>): string | null {
  const candidates = confirmedOf(input.blocksByDate[addDays(input.date, -1)])
    .filter((block) => block.data.endMin > CONTINUE_AFTER_MIN && activeIds.has(block.data.projectId))
    .sort((a, b) => b.data.endMin - a.data.endMin || (a.id < b.id ? -1 : 1));
  return candidates[0]?.data.projectId ?? null;
}

/** The active project not seen for more than `REVISIT_AFTER_DAYS` days whose last sighting is oldest. */
function revisitProject(input: SuggestInput, active: readonly LiveProject[]): string | null {
  const lastSeen = new Map<string, string>();
  for (const [date, blocks] of Object.entries(input.blocksByDate)) {
    if (date > input.date) continue;
    for (const block of confirmedOf(blocks)) {
      const previous = lastSeen.get(block.data.projectId);
      if (previous === undefined || previous < date) lastSeen.set(block.data.projectId, date);
    }
  }
  const stale = active
    .map((project) => ({ project, seen: lastSeen.get(project.id) }))
    .filter(
      (entry): entry is { project: LiveProject; seen: string } =>
        entry.seen !== undefined && daysBetween(entry.seen, input.date) > REVISIT_AFTER_DAYS,
    )
    .sort(
      (a, b) =>
        (a.seen < b.seen ? -1 : a.seen > b.seen ? 1 : 0) ||
        a.project.data.sort - b.project.data.sort ||
        (a.project.id < b.project.id ? -1 : 1),
    );
  return stale[0]?.project.id ?? null;
}

/**
 * Up to four one-click suggestions, at most one per kind and in the order fill, continue,
 * revisit, confirm. The first three target the current slot — the free part of the current
 * default-duration window (2026-10b §3.3), so a window partly covered by a live block (draft
 * or confirmed) still suggests its remainder of at least one time step; inactive projects are
 * never suggested.
 */
export function suggest(input: SuggestInput): Suggestion[] {
  const active = input.projects.filter(isLive).filter((project) => project.data.active);
  const activeIds = new Set(active.map((project) => project.id));
  const sortOf = new Map(active.map((project) => [project.id, project.data.sort]));
  const suggestions: Suggestion[] = [];

  const todayBlocks = liveOnly(input.blocksByDate[input.date] ?? []).map((block) => block.data);
  const slot = currentSlot(wallMinOf(input.nowMs, input.date), input.settings, todayBlocks);

  if (slot !== null) {
    const used = new Set<string>();
    const fill = fillSlotProject(input, slot, activeIds, sortOf);
    if (fill !== null) {
      used.add(fill);
      suggestions.push({ kind: SuggestionKind.FillSlot, projectId: fill, span: slot });
    }
    const keepGoing = continueProject(input, activeIds);
    if (keepGoing !== null && !used.has(keepGoing)) {
      used.add(keepGoing);
      suggestions.push({ kind: SuggestionKind.Continue, projectId: keepGoing, span: slot });
    }
    const revisit = revisitProject(input, active);
    if (revisit !== null && !used.has(revisit)) {
      suggestions.push({ kind: SuggestionKind.Revisit, projectId: revisit, span: slot });
    }
  }
  if (input.draftsPending > 0) {
    suggestions.push({ kind: SuggestionKind.ConfirmDrafts, count: input.draftsPending });
  }
  return suggestions.slice(0, MAX_SUGGESTIONS);
}
